import React from "react";
import { money, quantity as qty, taxOn } from "@/lib/money";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import QuotaChokeCard from "@/components/billing/QuotaChokeCard";
import { Badge, Button, Card, Icon, Input, Textarea } from "@/ds";
import { anonymousKeyFor, executeAction, rejectAction } from "@/lib/dany";
import { formatDate } from "@/lib/dates";
import DateField from "@/components/forms/DateField";

/**
 * The FixyBee's quote, as a card in the conversation (DY2.2).
 *
 * Same contract as the job draft: everything rendered comes from
 * `tool_result.data`, the `action_id` is the whole authorization, and the Send
 * button is the commit point. What is different here is what pressing it costs
 * — one of the FixyBee's monthly quote credits, or one purchased extra — so
 * this card says so before it is pressed rather than after.
 *
 * **The total is never typed and never trusted from here.** Each line's
 * subtotal and the grand total are recomputed in the browser as the FixyBee
 * edits, purely so the number under their thumb is the real one; the number
 * that reaches the quote is computed a third time inside
 * `routes.quotes.send_quote`, which ignores anything we send. Three
 * computations of the same arithmetic that cannot disagree, because only the
 * last one is authoritative.
 *
 * Four states:
 *   blocked  -> no credits: the two ways forward, and no Send button at all
 *   draft    -> editable lines with Send
 *   sent     -> the receipt, with what the send left on the meter
 *   discarded-> nothing
 */

const MAX_LINES = 20;
const MAX_DESCRIPTION = 200;

// N23: the local one grouped nothing, so a floor at 1,150 square feet showed
// as 17250.00 on the card the FixyBee reads the amount off. `lib/money` is the
// one place that decides how an amount is written; the `$` moves inside it,
// which is why the call sites below lose theirs.

/**
 * Subtotals, tax and total, from the lines as they stand on screen.
 *
 * N34 added the last two. The card had no tax anywhere while
 * `routes.quotes.send_quote` applied the FixyBee's saved rate, so the total
 * here and the total the client received were different numbers and nothing
 * said so. Recomputed on every edit rather than read off the draft for the
 * reason the subtotal already is: a stale figure on a card people read as the
 * price is the one number this flow exists to get right.
 */
function totals(lines, rate) {
  const priced = lines.map((line) => {
    const quantity = Number(line.quantity);
    const unit = Number(line.unit_price);
    const subtotal =
      Number.isFinite(quantity) && Number.isFinite(unit)
        ? Math.round(quantity * unit * 100) / 100
        : 0;
    return { ...line, subtotal };
  });
  const grand =
    Math.round(priced.reduce((sum, line) => sum + line.subtotal, 0) * 100) / 100;
  const tax = taxOn(rate, grand);
  return {
    lines: priced,
    grand,
    tax,
    due: Math.round((grand + (tax || 0)) * 100) / 100,
  };
}

export default function DanyQuoteWidget({ data, threadId, onCommitted }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dateId = React.useId();
  // T21-M5: half-typed dates are not "no dates" — the card does not send them.
  const [dateValid, setDateValid] = React.useState({ start: true, end: true });

  const [draft, setDraft] = React.useState(() => ({
    ...(data?.draft || {}),
    line_items: [...((data?.draft || {}).line_items || [])],
  }));
  const [busy, setBusy] = React.useState(false);
  const [editing, setEditing] = React.useState(false);
  const [phase, setPhase] = React.useState(
    data?.blocked === "no_credits" ? "blocked" : "draft"
  );
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState(null);
  // What a refusal at send time said about the way forward (PLANES M4c); the
  // snapshot below says the same at propose time.
  const [chokeDetail, setChokeDetail] = React.useState(null);

  const actionId = data?.action_id;
  const missing = data?.missing || [];
  const credits = data?.credits || null;
  const { lines, grand, tax, due } = totals(
    draft.line_items || [], draft.tax_rate ?? null);

  // The last credit is worth one quiet line, not a warning. Anything above one
  // is not news. Every plan has a number since PLANES (Master is 25).
  const lastOne = credits && credits.remaining === 1;

  const localProblem = React.useMemo(() => {
    if (!lines.length) return t("dany.quote.errorNoLines");
    if (lines.some((line) => !String(line.description || "").trim())) {
      return t("dany.quote.errorLineDescription");
    }
    if (lines.some((line) => !(Number(line.quantity) > 0))) {
      return t("dany.quote.errorQuantity");
    }
    if (lines.some((line) => !(Number(line.unit_price) >= 0))) {
      return t("dany.quote.errorPrice");
    }
    if (!(grand > 0)) return t("dany.quote.errorTotal");
    if (!(dateValid.start && dateValid.end)) return t("dateField.invalid");
    if (draft.proposed_start_date && draft.proposed_end_date
        && draft.proposed_end_date < draft.proposed_start_date) {
      return t("dany.quote.errorDateOrder");
    }
    return null;
  }, [lines, grand, draft.proposed_start_date, draft.proposed_end_date, dateValid, t]);

  const setLine = (index, key, value) => {
    const next = [...(draft.line_items || [])];
    next[index] = { ...next[index], [key]: value };
    setDraft({ ...draft, line_items: next });
  };

  const send = async () => {
    if (!actionId) return;
    setBusy(true);
    setError(null);
    try {
      // The subtotals go with it so the audit row records what the FixyBee was
      // looking at. The endpoint recomputes them anyway.
      // `tax_rate` rides along so the endpoint stores the rate this card
      // showed rather than re-reading the profile (N34). The amount is not
      // sent: the server recomputes it, as it does every figure on a quote.
      const payload = {
        ...draft, line_items: lines, grand_total: grand,
        tax_rate: draft.tax_rate ?? null,
      };
      const answer = await executeAction(actionId, payload, {
        anonymousKey: anonymousKeyFor(threadId),
      });
      setResult(answer);
      setPhase("sent");
      // The thread re-reads itself: this card is not the only thing on
      // screen that this press changed (T11 N38).
      onCommitted?.();
    } catch (err) {
      if (err.code === "no_credits") {
        // Lost the race for the last credit, or bought nothing since Dany read
        // the count. Either way the card stops being a quote and becomes the
        // two ways forward — the same thing it would have been at propose time.
        setChokeDetail(err.detail || null);
        setPhase("blocked");
      } else {
        setError(
          t([`dany.quote.${err.code}`, `dany.errors.${err.code}`,
             "dany.quote.sendFailed"], {
            defaultValue: t("dany.quote.sendFailed"),
          })
        );
      }
    } finally {
      setBusy(false);
    }
  };

  const discard = async () => {
    setBusy(true);
    try {
      await rejectAction(actionId, { anonymousKey: anonymousKeyFor(threadId) });
    } catch { /* the card goes away either way */ }
    setPhase("discarded");
    // The thread re-reads itself: this card is not the only thing on
    // screen that this press changed (T11 N38).
    onCommitted?.();
    setBusy(false);
  };

  if (phase === "discarded") return null;

  if (phase === "blocked") {
    // The same card the quote form shows (PLANES M4c): an extra quote at its
    // price, "upgrade", and the refill day — not an error and not a wall.
    // With no subscription at all there is nothing to top up, so that case
    // keeps its own sentence.
    if (!credits && !chokeDetail) {
      return (
        <Card variant="raised" padding="md" data-testid="dany-quote-no-credits">
          <p style={heading}>{t("dany.quote.noCreditsTitle")}</p>
          <p style={body}>{t("dany.quote.noCreditsBodyNoPlan")}</p>
        </Card>
      );
    }
    return (
      <div data-testid="dany-quote-no-credits">
        <QuotaChokeCard
          extraPrice={chokeDetail?.extra_price_usd ?? credits?.extra_price_usd ?? null}
          resetsAt={chokeDetail?.resets_at || credits?.resets_at || null}
          extraMax={chokeDetail?.extra_max_per_purchase ?? credits?.extra_max_per_purchase ?? null}
          canUpgrade={(chokeDetail?.upgrade_available ?? credits?.upgrade_available) !== false}
          testId="dany-quote-choke"
          fieldBelowOnSmallPhones
        />
      </div>
    );
  }

  if (phase === "sent") {
    const quote = result?.quote || {};
    const info = result?.credit_info || {};
    return (
      <Card variant="raised" padding="md" data-testid="dany-quote-sent">
        <p style={heading}>{t("dany.quote.sentTitle")}</p>
        <p style={body}>
          {t("dany.quote.sentBody", {
            total: money(quote.total_due, "$0.00"),
            job: draft.job_title || "",
          })}
        </p>
        {/* The meter after the spend, said once. `included` is always a number
            since PLANES; the guard stays for a send that returned no info.
            Extra quotes are mentioned only when there are some (PLANES-3). */}
        {info.included != null && (
          <p style={{ ...body, color: "var(--text-subtle)" }}
             data-testid="dany-quote-credits-left">
            {t("dany.quote.creditsAfter", {
              used: info.used, included: info.included,
            })}
            {info.extras > 0 && ` · ${t("dany.quote.extrasLeft", { count: info.extras })}`}
          </p>
        )}
        <Button variant="secondary" size="sm" iconRight="arrow-right"
                style={{ marginTop: "var(--space-4)" }}
                onClick={() => navigate("/provider/quotes")}>
          {t("dany.quote.viewQuotes")}
        </Button>
      </Card>
    );
  }

  return (
    <Card variant="raised" padding="md" data-testid="dany-quote-draft">
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: "var(--space-2)",
      }}>
        <span style={eyebrow}>
          <Icon name="file-text" size={12} style={{ color: "var(--hive-ink)" }} />
          {t("dany.quote.draftHeader")}
        </span>
        {credits && (
          <Badge tone="neutral">
            {t("dany.quote.creditsLeft", { n: credits.remaining })}
          </Badge>
        )}
      </div>

      {draft.job_title && (
        <h3 style={{
          marginTop: "var(--space-3)", fontFamily: "var(--font-display)",
          fontWeight: "var(--weight-bold)", fontSize: "var(--text-xl)",
          color: "var(--text-body)", letterSpacing: "var(--tracking-tight)",
        }}>{draft.job_title}</h3>
      )}

      <div style={{ marginTop: "var(--space-4)" }}>
        <span style={fieldLabel}>{t("dany.quote.lineItems")}</span>
        {lines.map((line, index) => (
          <div key={index} data-testid={`dany-quote-line-${index}`}
               style={{
                 display: "flex", gap: "var(--space-2)", alignItems: "flex-end",
                 marginTop: "var(--space-2)", flexWrap: "wrap",
               }}>
            {editing ? (
              <>
                <div style={{ flex: "3 1 200px" }}>
                  <Input
                    value={line.description || ""} maxLength={MAX_DESCRIPTION}
                    placeholder={t("dany.quote.lineDescription")}
                    onChange={(e) => setLine(index, "description", e.target.value)}
                    data-testid={`dany-quote-line-desc-${index}`}
                  />
                </div>
                <div style={{ flex: "0 1 80px" }}>
                  <Input
                    type="number" min="0" step="0.01" value={line.quantity ?? 1}
                    placeholder={t("dany.quote.lineQuantity")}
                    onChange={(e) => setLine(index, "quantity", e.target.value)}
                    data-testid={`dany-quote-line-qty-${index}`}
                  />
                </div>
                <div style={{ flex: "0 1 110px" }}>
                  <Input
                    type="number" min="0" step="0.01" value={line.unit_price ?? 0}
                    placeholder={t("dany.quote.lineUnitPrice")}
                    onChange={(e) => setLine(index, "unit_price", e.target.value)}
                    data-testid={`dany-quote-line-unit-${index}`}
                  />
                </div>
                <Button
                  variant="ghost" size="sm" icon="x"
                  disabled={lines.length <= 1}
                  aria-label={t("dany.quote.removeLine")}
                  title={t("dany.quote.removeLine")}
                  onClick={() => setDraft({
                    ...draft,
                    line_items: (draft.line_items || []).filter((_, i) => i !== index),
                  })}
                  data-testid={`dany-quote-line-remove-${index}`}
                />
              </>
            ) : (
              <>
                <span style={{
                  flex: "1 1 auto", fontSize: "var(--text-sm)",
                  color: "var(--text-body)",
                }}>
                  {line.description}
                  {Number(line.quantity) !== 1 && (
                    /* N23. This showed the bare quantity — "× 1150" — and the
                       unit price appeared nowhere on the card, so a FixyBee
                       reading back a floor at 1,150 square feet and fifteen
                       dollars a foot could see neither of the two numbers they
                       had dictated, only the product. Both, grouped, in the
                       shape a quote is written in. */
                    <span style={{ color: "var(--text-subtle)" }}>
                      {" "}{qty(line.quantity)} × {money(line.unit_price, "$0.00")}
                    </span>
                  )}
                </span>
                <span style={{
                  fontFamily: "var(--font-mono)", fontSize: "var(--text-sm)",
                  color: "var(--text-body)",
                }}>{money(line.subtotal, "$0.00")}</span>
              </>
            )}
          </div>
        ))}
        {editing && lines.length < MAX_LINES && (
          <Button
            variant="ghost" size="sm" icon="plus"
            style={{ marginTop: "var(--space-2)" }}
            onClick={() => setDraft({
              ...draft,
              line_items: [...(draft.line_items || []),
                           { description: "", quantity: 1, unit_price: 0 }],
            })}
            data-testid="dany-quote-add-line"
          >{t("dany.quote.addLine")}</Button>
        )}
      </div>

      {/* N34. The tax the client will be charged, named and attributed, above
          the total it is part of. Drawn only when there IS a rate above zero:
          a "Tax 0%: $0.00" line on a quote with no tax is an invoice this
          platform did not write, and a FixyBee who has never named a rate has
          no tax rather than a tax of nothing (T4b).

          Read-only, like every other figure on this card. Changing it is
          saying so — "ponle 8.25 de impuesto" — which is the same single
          writer the job draft has, and it changes THIS quote and never their
          saved default. */}
      {tax !== null && tax > 0 && (
        <div style={{ marginTop: "var(--space-2)" }} data-testid="dany-quote-tax">
          <div style={breakdownRow}>
            <span>{t("dany.quote.subtotal")}</span>
            <span style={breakdownNum}>{money(grand, "$0.00")}</span>
          </div>
          <div style={breakdownRow}>
            <span>{t("dany.quote.tax", { rate: draft.tax_rate })}</span>
            <span style={breakdownNum}>{money(tax, "$0.00")}</span>
          </div>
        </div>
      )}

      <div style={{
        marginTop: "var(--space-4)", paddingTop: "var(--space-3)",
        borderTop: "1px solid var(--border-subtle)", display: "flex",
        justifyContent: "space-between", alignItems: "baseline",
      }}>
        <span style={fieldLabel}>{t("dany.quote.total")}</span>
        <span style={{
          fontFamily: "var(--font-mono)", fontWeight: "var(--weight-semibold)",
          fontSize: "var(--text-2xl)", color: "var(--text-body)",
        }} data-testid="dany-quote-total">{money(due, "$0.00")}</span>
      </div>

      {editing && (
        <div style={{ marginTop: "var(--space-4)" }}>
          <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 140px" }}>
              <label htmlFor={`${dateId}-start`} style={fieldLabel}>{t("dany.quote.startDate")}</label>
              <DateField
                id={`${dateId}-start`} InputComponent={Input}
                value={draft.proposed_start_date || ""}
                onChange={(proposed_start_date) => setDraft((d) => ({ ...d, proposed_start_date }))}
                onValidityChange={(start) => setDateValid((v) => ({ ...v, start }))}
                data-testid="dany-quote-start"
              />
            </div>
            <div style={{ flex: "1 1 140px" }}>
              <label htmlFor={`${dateId}-end`} style={fieldLabel}>{t("dany.quote.endDate")}</label>
              <DateField
                id={`${dateId}-end`} InputComponent={Input}
                value={draft.proposed_end_date || ""}
                onChange={(proposed_end_date) => setDraft((d) => ({ ...d, proposed_end_date }))}
                onValidityChange={(end) => setDateValid((v) => ({ ...v, end }))}
                data-testid="dany-quote-end"
              />
            </div>
          </div>
          <label style={{ display: "block", marginTop: "var(--space-3)" }}>
            <span style={fieldLabel}>{t("dany.quote.paymentTerms")}</span>
            <Input
              value={draft.payment_terms || ""} maxLength={1000}
              onChange={(e) => setDraft({ ...draft, payment_terms: e.target.value })}
              data-testid="dany-quote-payment-terms"
            />
          </label>
          <label style={{ display: "block", marginTop: "var(--space-3)" }}>
            <span style={fieldLabel}>{t("dany.quote.additionalTerms")}</span>
            <Textarea
              value={draft.additional_terms || ""} maxLength={1000} rows={3}
              onChange={(e) => setDraft({ ...draft, additional_terms: e.target.value })}
              data-testid="dany-quote-additional-terms"
            />
          </label>
        </div>
      )}

      {!editing && (draft.proposed_start_date || draft.payment_terms) && (
        <div style={{
          marginTop: "var(--space-3)", display: "flex", gap: "var(--space-4)",
          flexWrap: "wrap", fontFamily: "var(--font-mono)",
          fontSize: "var(--text-xs)", color: "var(--text-muted)",
        }}>
          {draft.proposed_start_date && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              <Icon name="calendar" size={11} />
              {t("dany.quote.datesRange", {
                start: formatDate(draft.proposed_start_date),
                end: formatDate(draft.proposed_end_date || draft.proposed_start_date),
              })}
            </span>
          )}
          {draft.payment_terms && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              <Icon name="wallet" size={11} />{draft.payment_terms}
            </span>
          )}
        </div>
      )}

      {missing.length > 0 && (
        <p style={{ ...body, color: "var(--text-muted)" }}
           data-testid="dany-quote-missing">
          {t("dany.quote.missing", {
            fields: missing
              .map((f) => t(`dany.quote.field${f[0].toUpperCase()}${f.slice(1)}`))
              .join(", "),
          })}{" "}{t("dany.quote.missingHint")}
        </p>
      )}
      {lastOne && !localProblem && (
        <p style={{ ...body, color: "var(--text-subtle)" }}
           data-testid="dany-quote-last-credit">
          {t("dany.quote.lastCredit")}
        </p>
      )}
      {(localProblem || error) && (
        <p style={{ ...body, color: "var(--danger)" }}
           data-testid="dany-quote-problem">{error || localProblem}</p>
      )}

      <div style={{
        marginTop: "var(--space-5)", display: "flex", gap: "var(--space-2)",
        flexWrap: "wrap",
      }}>
        <Button variant="primary" size="sm" iconRight="arrow-right"
                disabled={busy || !!localProblem || !actionId}
                onClick={send} data-testid="dany-quote-send">
          {busy ? t("dany.quote.sending") : t("dany.quote.send")}
        </Button>
        <Button variant="ghost" size="sm" icon="pencil" disabled={busy}
                onClick={() => setEditing((value) => !value)}
                data-testid="dany-quote-edit">
          {editing ? t("dany.quote.done") : t("dany.quote.edit")}
        </Button>
        <Button variant="ghost" size="sm" icon="x" disabled={busy}
                onClick={discard} data-testid="dany-quote-discard">
          {t("dany.quote.discard")}
        </Button>
      </div>
    </Card>
  );
}

const breakdownRow = {
  display: "flex", justifyContent: "space-between", alignItems: "baseline",
  fontSize: "var(--text-sm)", color: "var(--text-muted)",
  marginTop: "var(--space-1)",
};

const breakdownNum = { fontFamily: "var(--font-mono)" };

const fieldLabel = {
  display: "block", marginBottom: 4, fontFamily: "var(--font-display)",
  fontSize: "var(--text-xs)", textTransform: "uppercase",
  letterSpacing: "var(--tracking-widest)", color: "var(--dany-meta)",
};

const eyebrow = {
  display: "inline-flex", alignItems: "center", gap: 6,
  fontFamily: "var(--font-display)", fontSize: "var(--text-xs)",
  textTransform: "uppercase", letterSpacing: "var(--tracking-widest)",
  color: "var(--dany-meta)",
};

const heading = {
  fontFamily: "var(--font-display)", fontWeight: "var(--weight-bold)",
  fontSize: "var(--text-lg)", color: "var(--text-body)",
};

const body = {
  marginTop: "var(--space-2)", fontSize: "var(--text-sm)",
  color: "var(--text-muted)", lineHeight: "var(--leading-relaxed)",
  maxWidth: "var(--measure-body)",
};
